import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchContent, fetchLibrary, fetchMyList, addToMyList, removeFromMyList, saveProgress } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VideoPlayer from "@/components/VideoPlayer";
import CategoryRow from "@/components/CategoryRow";
import { ChevronLeft, Clock, Star, Plus, Check } from "lucide-react";
import { toast } from "sonner";

export default function Watch() {
  const { id } = useParams();
  const { user } = useAuth() || {};
  const [content, setContent] = useState(null);
  const [related, setRelated] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setContent(null);
    fetchContent(id).then(c => { setContent(c); fetchLibrary({ category: c.category }).then(r => setRelated(r.filter(x => x.id !== c.id))); });
  }, [id]);

  useEffect(() => {
    if (user) fetchMyList().then(list => setSaved(list.some(i => i.id === id))).catch(()=>{});
  }, [user, id]);

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save"); return; }
    if (saved) { await removeFromMyList(id); setSaved(false); toast.success("Removed"); }
    else { await addToMyList(id); setSaved(true); toast.success("Added to My List"); }
  };

  const onProgress = (pos, dur) => { if (user) saveProgress(id, pos, dur).catch(()=>{}); };

  if (!content) return <div className="pt-24 text-white/60 text-center">Loading…</div>;

  return (
    <div className="pt-16" data-testid="watch-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <Link to="/live" className="inline-flex items-center gap-1 text-white/60 hover:text-[#D4AF37] mb-6 text-sm" data-testid="back-link"><ChevronLeft className="w-4 h-4" />Back</Link>
        <VideoPlayer src={content.video_url} streamType={content.stream_type || "mp4"} poster={content.thumbnail} title={content.title} subtitle={content.category} liveBadge={false} onProgress={onProgress} />

        <div className="mt-8 grid md:grid-cols-[1fr_320px] gap-8">
          <div>
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.25em] uppercase mb-2">{content.category}</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold leading-tight mb-4">{content.title}</h1>
            <div className="flex items-center gap-4 text-sm text-white/60 mb-6">
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-[#D4AF37]" fill="currentColor" />{content.rating}</span>
              <span>{content.year}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{content.duration_min}m</span>
            </div>
            <p className="text-white/80 leading-relaxed max-w-2xl mb-6">{content.description}</p>
            <button onClick={toggleSave} data-testid="save-to-list" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${saved ? "bg-[#D4AF37] text-[#050507]" : "btn-ghost"}`}>
              {saved ? <><Check className="w-4 h-4"/>Saved to My List</> : <><Plus className="w-4 h-4"/>Add to My List</>}
            </button>
          </div>
          <div className="bg-[#0A0A0E] border border-white/10 rounded-xl p-5">
            <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-white/50 mb-3">Details</p>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-white/50">Category</dt><dd>{content.category}</dd></div>
              <div className="flex justify-between"><dt className="text-white/50">Year</dt><dd>{content.year}</dd></div>
              <div className="flex justify-between"><dt className="text-white/50">Rating</dt><dd>{content.rating}</dd></div>
              <div className="flex justify-between"><dt className="text-white/50">Runtime</dt><dd>{content.duration_min} min</dd></div>
            </dl>
          </div>
        </div>

        {related.length > 0 && <div className="mt-16"><CategoryRow title="More Like This" items={related} /></div>}
      </div>
    </div>
  );
}
